import * as React from 'react';

import { ThemeColor } from '@rematter/ui-web';
import { Node, NodeProps, Position } from '@xyflow/react';
import { LucideIcon } from 'lucide-react';

/**
 * NodeType enum matching the backend GraphQL enum
 * This is the single source of truth for node types
 */
export type NodeType =
  // Triggers
  | 'HttpWebhookTrigger'
  | 'BusinessEventTrigger'
  | 'ScheduleTrigger'
  // Actions
  | 'EmailAction'
  | 'HttpRequestAction'
  // Flow Control
  | 'Condition'
  | 'Delay'
  | 'Approval'
  | 'Filter'
  | 'Switch'
  | 'Loop';

/**
 * Helper to check if a node type is a trigger
 */
export const isTriggerNodeType = (type: string): boolean =>
  type === 'HttpWebhookTrigger' ||
  type === 'BusinessEventTrigger' ||
  type === 'ScheduleTrigger';

/**
 * Helper to check if a node type is an action
 */
export const isActionNodeType = (type: string): boolean =>
  type === 'EmailAction' || type === 'HttpRequestAction';

/**
 * Base data structure that ALL automation nodes share
 */
export type BaseNodeData = {
  /** Display label shown in the node */
  label: string;
  /** Optional description */
  description?: string;
  /** Node-specific configuration - typed per node type */
  config: Record<string, unknown>;
};

/**
 * Data for trigger nodes (BusinessEvent, HttpWebhook, Schedule)
 */
export type TriggerNodeData<TConfig = Record<string, unknown>> =
  BaseNodeData & {
    config: TConfig;
  };

/**
 * Data for action nodes (Email, HttpRequest, etc.)
 */
export type ActionNodeData<TConfig = Record<string, unknown>> = BaseNodeData & {
  config: TConfig;
};

/**
 * Data for flow control nodes (Condition, Delay)
 */
export type FlowNodeData<TConfig = Record<string, unknown>> = BaseNodeData & {
  config: TConfig;
};

/**
 * Union type for all automation node data.
 * Use this as the default generic type for Node<T>.
 */
export type AutomationNodeData =
  | TriggerNodeData
  | ActionNodeData
  | FlowNodeData;

/**
 * Typed automation node using React Flow's Node type
 */
export type AutomationNode = Node<AutomationNodeData>;

/**
 * Typed Node for trigger nodes - use with NodeProps<TriggerNode<TConfig>>
 */
export type TriggerNode<TConfig = Record<string, unknown>> = Node<
  TriggerNodeData<TConfig>
>;

/**
 * Typed Node for action nodes - use with NodeProps<ActionNode<TConfig>>
 */
export type ActionNode<TConfig = Record<string, unknown>> = Node<
  ActionNodeData<TConfig>
>;

/**
 * Typed Node for flow control nodes - use with NodeProps<FlowNode<TConfig>>
 */
export type FlowNode<TConfig = Record<string, unknown>> = Node<
  FlowNodeData<TConfig>
>;

/**
 * Configuration for a node handle (connection point)
 */
export type HandleConfig = {
  type: 'source' | 'target';
  position: Position;
  id?: string;
  className?: string;
};

/**
 * Category for grouping nodes in the palette
 */
export type NodeCategory = 'trigger' | 'action' | 'flow';

/**
 * Props passed to config panel components
 */
export type NodeConfigProps<TConfig = Record<string, unknown>> = {
  config: Partial<TConfig>;
  onUpdate: (updates: Partial<TConfig>) => void;
  /** Node ID for LSP document URI */
  nodeId?: string;
};

/**
 * Base node definition type used for registry storage.
 * Uses Record<string, unknown> for config to allow heterogeneous collections.
 */
export type BaseNodeDefinition = {
  /** Node type key - matches the backend GraphQL NodeType enum */
  key: NodeType;
  category: NodeCategory;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  paletteGroup?: string;
  NodeComponent: React.ComponentType<NodeProps<Node<BaseNodeData>>>;
  ConfigComponent: React.ComponentType<
    NodeConfigProps<Record<string, unknown>>
  >;
  handles: HandleConfig[];
  defaultConfig: () => Record<string, unknown>;
  validateConfig: (config: Record<string, unknown>) => Record<string, string>;
  getPreviewText?: (config: Record<string, unknown>) => string;
};

/**
 * Generic node definition for type-safe individual definitions.
 * Use this when defining a specific node type with its config.
 */
export type NodeDefinition<TConfig extends Record<string, unknown>> = {
  /** Node type key - matches the backend GraphQL NodeType enum */
  key: NodeType;
  category: NodeCategory;
  label: string;
  description: string;
  icon: LucideIcon;
  color: ThemeColor;
  paletteGroup?: string;
  NodeComponent: React.ComponentType<NodeProps<Node<BaseNodeData>>>;
  ConfigComponent: React.ComponentType<NodeConfigProps<TConfig>>;
  handles: HandleConfig[];
  defaultConfig: () => TConfig;
  validateConfig: (config: TConfig) => Record<string, string>;
  getPreviewText?: (config: TConfig) => string;
};

/**
 * Type helper to extract config type from a node definition
 */
export type ConfigTypeOf<T> = T extends NodeDefinition<infer C> ? C : never;

/**
 * Visual definition for nodes - used by BaseNode for rendering.
 * This is a subset of BaseNodeDefinition containing only the visual properties.
 */
export type NodeVisualDefinition = {
  key: NodeType;
  label: string;
  description: string;
  icon: LucideIcon;
  color: ThemeColor;
  handles: HandleConfig[];
  getContent?: (config: Record<string, unknown>) => string;
};
